import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { type RankedAttempt, attemptHeading } from '@/lib/session-analysis';
import { attemptCause } from '@/lib/session-noyau';

/**
 * The attempts of the analyst, call by call, and what was wrong with each one
 * (noyau.md §9.1: « pour l'échec, la cause de chaque essai »).
 *
 * Drawn when a determination FAILED, and since #196 when the second pass failed
 * twice and the notes are the compatibility's alone (« style absent »): the
 * attempts shown are then the second pass's, under the yellow line that says so.
 * Each attempt is headed by its pass - « Passe 1 · essai 2 » - and carries the
 * fingerprint of the system IT was served; one of before #196, which had no
 * passes, keeps « Essai N ».
 *
 * The answer as it arrived is the cause - wrapped and never re-serialised
 * (`layer-brut.tsx`): a JSON of one line would otherwise hide its second half off
 * the edge of the block.
 */

/**
 * A fingerprint in fixed pitch (N39), or « sans empreinte » on a call recorded
 * before #154. On the line of the instrument of the analyst in two passes the word
 * is the instrument's (`empreinte de l'instrument`, K11); on an attempt it is the
 * system of that call.
 */
export function Fingerprint({
  value,
  label = 'empreinte',
}: { value: string | null; label?: string }) {
  return value === null ? (
    <span>sans empreinte</span>
  ) : (
    <span>
      {label}{' '}
      <code
        data-testid={SESSIONS_TEST_IDS.walkNoyauFingerprint}
        className="font-mono text-gray-900 text-xs"
      >
        {value}
      </code>
    </span>
  );
}

export function NoyauAttempts({ attempts }: { attempts: readonly RankedAttempt[] }) {
  if (attempts.length === 0) return null;
  return (
    <ol className="flex flex-col gap-2">
      {attempts.map((attempt, index) => {
        const { call, pass } = attempt;
        return (
          <li
            // By position: a determination is a call and its retry, in order.
            key={`${call.startedAt}-${index}`}
            data-testid={SESSIONS_TEST_IDS.walkNoyauAttempt}
            data-pass={pass ?? undefined}
            className="flex flex-col gap-1.5 text-caption"
          >
            <p className="text-gray-700">
              <b className="font-semibold text-gray-900">{attemptHeading(attempt)}</b>
              {call.model === null ? '' : ` · ${call.model}`} ·{' '}
              <span className="text-jaune-deep">
                {attemptCause(call) ?? (pass === null ? 'Notes rendues.' : 'Réponse lue.')}
              </span>
              {/* Each attempt carries its own fingerprint (§9.1) - the retry keeps the
                  system, so the two of a pass agree unless something broke. */}
              {' · '}
              <Fingerprint value={call.promptFingerprint} />
            </p>
            {call.output === '' ? null : (
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md border border-gray-200 bg-gray-50 p-2.5 font-mono text-gray-900 text-xs leading-5">
                {call.output}
              </pre>
            )}
          </li>
        );
      })}
    </ol>
  );
}
